<template>
  <div v-if="!isOfflineVersion" id="pathisis">

    <div v-if="loading" class="tw-text-center tw-py-5">
      <b-spinner variant="primary" />
    </div>

    <div v-else-if="state ==='PATHISIS_LIST'">

      <!-- Mobile/tablet: search + category dropdown -->
      <div class="tw-block lg:tw-hidden tw-mb-3">
        <b-form-input
            v-model="searchQuery"
            :placeholder="$t('searchPlaceholder')"
            type="search"
            debounce="300"
            class="tw-mb-2"
            @update="performSearch"
        />
        <b-form-select
            v-if="!isSearching"
            :value="selectedCategory"
            :options="categoryOptions"
            @change="switchCategory"
        />
      </div>

      <div class="row tw-max-h-[65vh]">
        <!-- Desktop sidebar -->
        <div class="col-lg-4 pathisis-sidebar tw-hidden lg:tw-block tw-max-h-[65vh] tw-overflow-y-auto">
          <b-form-group class="tw-mb-2">
            <b-form-input
                v-model="searchQuery"
                :placeholder="$t('searchPlaceholder')"
                type="search"
                debounce="300"
                @update="performSearch"
            />
          </b-form-group>

          <ul class="tw-pl-0 tw-list-none tw-m-0">
            <li v-for="category of categories" :key="category.id"
                :class="[
                  !isSearching && category.id == selectedCategory ? 'current-category' : '',
                  isSearching ? 'tw-opacity-40 tw-pointer-events-none' : ''
                ]"
                @click="switchCategory(category.id)"
            >
              {{ category.name }}
            </li>
          </ul>
        </div>

        <div class="col-lg-8 tw-max-h-[65vh] tw-overflow-y-auto tw-pb-3">

          <div v-if="isSearching" class="tw-mb-2">
            <small class="tw-text-gray-500">{{ filteredPathisis.length }} {{ $t('pathisis.resultsFound') }}</small>
          </div>

          <div v-if="filteredPathisis.length > 0" class="modern-list-items">
            <div v-for="item in filteredPathisis" :key="item.id" class="modern-list-item"
                 @click="handleItemClick(item)"
            >
              <div class="modern-list-item__main">
                <div class="modern-list-item__name">
                  <span class="modern-list-item__title">{{ item.title }}</span>
                  <span v-if="getExternalLink(item)" class="modern-list-badge modern-list-badge--soft">
                    <i class="fe-external-link" style="font-size: 0.55rem; margin-right: 2px;" />Link
                  </span>
                </div>
                <div v-if="isSearching && item._categoryName" class="modern-list-item__sub">
                  <span class="modern-list-type-chip">{{ item._categoryName }}</span>
                </div>
              </div>

              <div class="modern-list-item__actions">
                <b-button class="pathisis-arrow-btn" size="sm" variant="primary">
                  <i :class="getExternalLink(item) ? 'fe-external-link' : 'fe-arrow-right'" />
                </b-button>
              </div>
            </div>
          </div>

          <div v-else class="tw-text-center tw-py-8 tw-text-gray-400 tw-text-sm">
            {{ $t('pathisis.noResults') }}
          </div>

        </div>
      </div>
    </div>
    <div v-if="state === 'PATHISIS_SINGLE_PAGE'" class="row">
      <div class="pathisis-content col-md-12">

        <div class="tw-flex tw-items-center tw-gap-3 tw-mb-4">
          <b-button class="pathisis-back-btn" variant="light" @click="returnToList"><i class="fe-arrow-left"></i></b-button>
          <h5 class="tw-m-0 tw-font-semibold">{{ selectedPathisis.title }}</h5>
        </div>
        <b-tabs content-class="mt-1" nav-class="nav-tabs nav-bordered">
          <b-tab v-if="selectedPathisis.sustaseis_diatrofis" active :title="$t('pathisis.tabs.0')">
            <vue-markdown>{{ selectedPathisis.sustaseis_diatrofis }}</vue-markdown>
          </b-tab>
          <b-tab v-if="selectedPathisis.odigies_diaitomenou" :title="$t('pathisis.tabs.1')">
            <div class="tw-text-right tw-mb-2">
              <b-button variant="light" @click="printInstructions"><i class="fe-printer"></i> {{ $t('global.print') }}</b-button>
            </div>
            <vue-markdown>{{ selectedPathisis.odigies_diaitomenou }}</vue-markdown>
            <div v-show="isPrinting" id="print-area" ref="print-area">
              <div v-show="isPrinting" class="print-intro">
                <div class="print-intro-text">
                  <strong> {{ $t('date') }}: </strong>{{ formatDateWithoutHourAsLink() }}<br>
                  <span><strong>{{ $t('pathisis.dietitian') }}:</strong> {{ user.firstName + ' ' + user.lastName }} </span>
                </div>
                <img
                    :src="user.logo ? user.logo : 'https://edlservices.s3.eu-north-1.amazonaws.com/athlisis-icon-printing.png'"
                    class="print-logo"
                />
              </div>
              <div class="article-title">{{ $t('pathisis.instructions') }}:
                <strong>{{ selectedPathisis.title }}</strong></div>

              <div class="note-body">
                <vue-markdown>{{ selectedPathisis.odigies_diaitomenou }}</vue-markdown>
              </div>
            </div>
          </b-tab>
          <b-tab v-if="selectedPathisis.proteinomena_simpliromata" :title="$t('pathisis.tabs.2')">
            <vue-markdown>{{ selectedPathisis.proteinomena_simpliromata }}</vue-markdown>
          </b-tab>
          <b-tab v-if="selectedPathisis.diagnwstika_ergaleia" :title="$t('pathisis.tabs.3')">
            <vue-markdown>{{ selectedPathisis.diagnwstika_ergaleia }}</vue-markdown>
          </b-tab>
          <b-tab v-if="selectedPathisis.thewria_pathisis_gia_meleti" :title="$t('pathisis.tabs.4')">
            <vue-markdown>{{ selectedPathisis.thewria_pathisis_gia_meleti }}</vue-markdown>
          </b-tab>
        </b-tabs>
      </div>
    </div>

    <div class="tw-text-right tw-mt-5 tw-mr-2">
      <img src="@assets/images/pathisis.svg" width="175"/>
    </div>
  </div>
  <educational-unavailable v-else/>

</template>

<style lang="scss">
#pathisis {
  .pathisis-sidebar li {
    cursor: pointer;
    padding: 8px 16px;
    color: $color_text;
    font-size: 0.88rem;
    border-radius: 8px;
    transition: all 0.12s ease;

    &:hover:not(.tw-opacity-40) {
      background-color: rgba($color_primary, 0.04);
    }

    &.current-category {
      background-color: rgba($color_primary, 0.08);
      font-weight: 600;
      color: $color_primary;
    }
  }

  .pathisis-arrow-btn {
    border-radius: 50% !important;
    width: 32px !important;
    height: 32px !important;
    padding: 0 !important;
    display: inline-flex !important;
    align-items: center;
    justify-content: center;
    font-size: 0.85rem;
  }

  .pathisis-back-btn {
    border-radius: 50% !important;
    width: 34px !important;
    height: 34px !important;
    min-width: 34px;
    padding: 0 !important;
    display: inline-flex !important;
    align-items: center;
    justify-content: center;
    font-size: 0.9rem;
    color: $color_primary !important;
    border: 1px solid rgba($color_primary, 0.3) !important;
    background: #fff !important;

    &:hover {
      background: rgba($color_primary, 0.06) !important;
      border-color: $color_primary !important;
    }
  }

  .pathisis-content .tab-content {
    max-height: 65vh;
    overflow-y: auto;
    padding: 20px;
    font-size: 0.9rem;
    line-height: 1.7;

    img {
      max-width: 100%;
    }

    h1, h2, h3, h4, h5, h6 {
      margin-top: 1.25rem;
      margin-bottom: 0.5rem;
    }

    ul, ol {
      padding-left: 1.5rem;
    }
  }
}
</style>


<script>

import {mapState} from "vuex";
import {PATHISIS_SELECTED_CATEGORY} from "@utils/storageKeys";
import {
  checkIfUpdateRequired,
  fetchCategories,
  fetchAllItems,
  getItemsByCategory,
  search
} from "./pathisisService";
export default {

  components: {VueMarkdown: () => import(/* webpackPrefetch: true */ 'vue-markdown')},
  watch: {
    selectedCategory() {
      if (!this.isSearching) {
        this.updateCategoryItems();
      }
    }
  },
  computed: {
    ...mapState({
      user: state => state.auth.currentUser
    }),
    isSearching() {
      return this.searchQuery.trim().length > 0;
    },
    filteredPathisis() {
      if (!this.isSearching) return this.categoryItems;
      return this.searchResults;
    },
    categoryOptions() {
      return this.categories.map(cat => ({value: cat.id, text: cat.name}));
    }
  },
  data() {
    return {
      state: 'PATHISIS_LIST',
      categories: [],
      categoryItems: [],
      allItems: [],
      searchIndex: [],
      searchQuery: '',
      searchResults: [],
      selectedCategory: sessionStorage.getItem(PATHISIS_SELECTED_CATEGORY) || null,
      selectedPathisis: null,
      isPrinting: false,
      loading: true
    }
  },

  async mounted() {
    await checkIfUpdateRequired();

    this.categories = await fetchCategories();

    const storedCategoryExists = this.selectedCategory
        && this.categories.some(c => c.id == this.selectedCategory);

    if ((!this.selectedCategory || !storedCategoryExists) && this.categories.length > 0) {
      this.selectedCategory = this.categories[0].id;
    }

    const {allItems, searchIndex} = await fetchAllItems(this.categories);
    this.allItems = allItems;
    this.searchIndex = searchIndex;

    this.updateCategoryItems();
    this.loading = false;
  },

  methods: {

    updateCategoryItems() {
      this.categoryItems = getItemsByCategory(this.allItems, this.selectedCategory);
    },

    switchCategory(id) {
      this.searchQuery = '';
      this.searchResults = [];
      this.selectedCategory = id;
      sessionStorage.setItem(PATHISIS_SELECTED_CATEGORY, this.selectedCategory);
    },

    performSearch() {
      this.searchResults = search(this.searchQuery, this.searchIndex, this.allItems);
    },

    getExternalLink(item) {
      const fields = ['sustaseis_diatrofis', 'odigies_diaitomenou', 'proteinomena_simpliromata', 'diagnwstika_ergaleia', 'thewria_pathisis_gia_meleti'];
      const populated = fields.filter(f => item[f] && item[f].trim());
      if (populated.length !== 1) return null;

      const match = populated[0] && item[populated[0]].trim().match(/^\[.*?\]\((https?:\/\/[^)]+)\)$/);
      return match ? match[1] : null;
    },

    handleItemClick(item) {
      const link = this.getExternalLink(item);
      if (link) {
        window.open(link, '_blank', 'noopener');
      } else {
        this.viewPathisis(item);
      }
    },

    returnToList() {
      this.state = 'PATHISIS_LIST'
    },

    viewPathisis(pathisis) {
      const clean = {...pathisis};
      for (let key of Object.keys(clean)) {
        if (typeof clean[key] === 'string') {
          clean[key] = clean[key].replace(/&lt;/g, '<')
          clean[key] = clean[key].replace(/&gt;/g, '>')
          clean[key] = clean[key].replace(/&amp;/g, '&')
          clean[key] = clean[key].replace(/&quot;/g, '"')
          clean[key] = clean[key].replace(/&apos;/g, "'")
        }
      }
      this.selectedPathisis = clean;
      this.state = 'PATHISIS_SINGLE_PAGE'
    },

    printInstructions() {
      this.isPrinting = true;
      let divToPrint = this.$refs['print-area'].outerHTML;
      this.isPrinting = false;
      divToPrint = divToPrint.replaceAll("<p><br></p>", "");
      divToPrint = divToPrint.replaceAll('display: none', "");

      const newWin = window.open("", "", "toolbar=yes,scrollbars=yes,resizable=yes,top=500,left=500,width=900,height=900");
      newWin.document.write(`<html><head><title>${this.$t('pathisis.printInstructions')}: ${this.selectedPathisis.title}</title>`);
      newWin.document.write('<link rel="stylesheet" type="text/css" href="https://edlservices.s3.eu-north-1.amazonaws.com/athlisis-print-v2.css">');
      newWin.document.write(`<style type="text/css">
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700&display=swap');
        @page { margin: 0.5in; }
        body { font-family: 'Inter', -apple-system, sans-serif; color: #1a1a1a; line-height: 1.7; font-size: 14px; margin: 0; padding: 0; }
        .print-intro { display: flex !important; align-items: center; justify-content: space-between; padding: 10px 0; margin-bottom: 10px; }
        .print-intro-text { flex: 1; }
        .print-logo { max-height: 50px; width: auto; max-width: 140px; object-fit: contain; flex-shrink: 0; }
        .print-intro strong { font-weight: 600; }
        .article-category { display: none; }
        .article-title { background-color: #f5f5f5; padding: 10px 20px; text-align: center; margin-bottom: 18px; font-size: 14px; color: #333; }
        .note-body { padding: 0 8px; }
        .note-body h1, .note-body h2, .note-body h3, .note-body h4, .note-body h5 { margin-top: 1.2em; margin-bottom: 0.4em; color: #111827; }
        .note-body strong { font-weight: 600; }
        .note-body ul, .note-body ol { padding-left: 1.5em; margin: 0.5em 0; }
        .note-body li { margin-bottom: 0.3em; }
        img { max-width: 100% !important; max-height: 450px; }
      </style>`);
      newWin.document.write('</head><body>');
      newWin.document.write(divToPrint);
      newWin.document.write('</body></html>');
      newWin.document.close();

      newWin.onload = function () {
        setTimeout(function () {
          newWin.focus();
          newWin.onafterprint = function () { newWin.close(); };
          newWin.print();
        }, 500);
      };
    }

  }

}
</script>
